import { CalendarClock, CircleCheck, Radio, TriangleAlert } from "lucide-react";
import { MetricValue } from "@nexus/widget-sdk";
import { useDisplayTimezone } from "@nexus/ui";

import { formatClock, formatRelative, parseDbTime } from "@nexus/widget-kit";
import type { EventMonitorLabels } from "./labels";
import type { MonitoredEvent } from "./model";

/**
 * The situation in one line: how many events are live, whether any needs
 * attention, what comes next. Everything else is in the rows below.
 */
export function SituationStrip({
  live,
  attention,
  next,
  now,
  fetchedAt,
  labels,
}: {
  live: number;
  attention: number;
  next: MonitoredEvent | null;
  now: number;
  fetchedAt: string;
  labels: EventMonitorLabels;
}) {
  const timezone = useDisplayTimezone();
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pb-3">
      <div className="flex items-baseline gap-2">
        <MetricValue size="compact">{live}</MetricValue>
        <span className="text-metric-caption font-medium text-secondary">{labels.liveEvents}</span>
      </div>
      {attention > 0 ? (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-status-error-bg px-2.5 py-1 text-sm font-semibold text-status-error-text">
          <TriangleAlert className="size-4" aria-hidden />
          {attention === 1 ? "1 event needs attention" : `${attention} events need attention`}
        </span>
      ) : (
        <span className="inline-flex items-center gap-1.5 text-sm text-secondary">
          <CircleCheck className="size-4 text-status-ok-text" aria-hidden />
          {labels.allClear}
        </span>
      )}
      {next ? <NextEvent event={next} now={now} label={labels.next} /> : null}
      <span
        className="ml-auto text-xs tabular-nums text-muted"
        title="Time of the latest data refresh"
      >
        Updated {formatClock(parseDbTime(fetchedAt), timezone)}
      </span>
    </div>
  );
}

function NextEvent({ event, now, label }: { event: MonitoredEvent; now: number; label: string }) {
  return (
    <span className="flex min-w-0 items-center gap-1.5 text-sm text-muted">
      <CalendarClock className="size-4 shrink-0" aria-hidden />
      {label}
      <span className="truncate font-medium text-primary">{event.event_label}</span>
      <span className="shrink-0 tabular-nums">
        {formatRelative(now, parseDbTime(event.event_start))}
      </span>
    </span>
  );
}

/**
 * Nothing live: one quiet line, and the next few events as entry points —
 * useful at a glance, never a paragraph.
 */
export function QuietState({
  upcoming,
  now,
  labels,
  onOpen,
}: {
  upcoming: MonitoredEvent[];
  now: number;
  labels: EventMonitorLabels;
  onOpen?: (event: MonitoredEvent) => void;
}) {
  const timezone = useDisplayTimezone();
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-3 py-1">
        <span className="grid size-8 shrink-0 place-items-center rounded-full bg-surface-elevated text-muted">
          <Radio className="size-4" aria-hidden />
        </span>
        <p className="text-sm font-medium text-primary">
          {upcoming.length ? labels.emptyLive : labels.emptyAll}
        </p>
      </div>
      {upcoming.length ? (
        <div className="mt-3">
          <p className="px-2 pb-1 text-xs font-medium text-muted">{labels.upNext}</p>
          <ul className="divide-y divide-border/70">
            {upcoming.slice(0, 3).map((event) => (
              <li key={event.event_id}>
                <button
                  type="button"
                  disabled={!onOpen}
                  onClick={() => onOpen?.(event)}
                  className="grid w-full grid-cols-[4.5rem_minmax(0,1fr)_auto] items-center gap-x-4 rounded-md px-2 py-2 text-left text-sm transition-colors enabled:hover:bg-surface-elevated/60 focus-visible:ring-2 focus-visible:ring-focus focus-visible:outline-none"
                >
                  <span className="whitespace-nowrap tabular-nums text-secondary">
                    {formatClock(parseDbTime(event.event_start), timezone)}
                  </span>
                  <span className="truncate font-medium text-primary">{event.event_label}</span>
                  <span className="text-xs tabular-nums text-muted">
                    {formatRelative(now, parseDbTime(event.event_start))}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
